import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { Printer, FileDown } from 'lucide-react';
import { exportCSV } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

export default function IFTAReport() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(`Q${Math.ceil((new Date().getMonth() + 1) / 3)}`);
  const company = useCompanyInfo();

  useEffect(() => {
    client.entities.IFTAMileageLog.list('-trip_date', 1000).then(l => { setLogs(l); setLoading(false); });
  }, []);

  const filtered = logs.filter(l => l.year === year && l.quarter?.startsWith(quarter));

  const stateMap = {};
  filtered.forEach(l => {
    try {
      const miles = JSON.parse(l.state_mileage || '[]');
      miles.forEach(({ state, miles: m }) => {
        if (!stateMap[state]) stateMap[state] = { loaded: 0, empty: 0 };
        stateMap[state].loaded += parseFloat(m) || 0;
      });
    } catch {}
  });

  const rows = Object.entries(stateMap).sort((a, b) => a[0].localeCompare(b[0])).map(([state, v]) => ({
    State: state,
    'Loaded Miles': Math.round(v.loaded),
    'Empty Miles': Math.round(v.empty),
    'Total Miles': Math.round(v.loaded + v.empty),
  }));

  const totalMiles = rows.reduce((s, r) => s + r['Total Miles'], 0);

  const csvRows = rows.map(r => ({
    State: r.State,
    'Loaded Miles': r['Loaded Miles'],
    'Empty Miles': r['Empty Miles'],
    'Total Miles': r['Total Miles'],
  }));

  const handlePrint = () => {
    const cols = ['State', 'Loaded Miles', 'Empty Miles', 'Total Miles'];
    const dataRows = rows.map(r => [
      { value: r.State, class: 'bold' },
      r['Loaded Miles'].toLocaleString(),
      r['Empty Miles'].toLocaleString(),
      { value: r['Total Miles'].toLocaleString(), class: '' },
    ]);
    const totalsRow = ['TOTAL', '', '', { value: totalMiles.toLocaleString(), class: 'gold' }];
    const cards = [
      { label: 'States Operated', value: rows.length, color: 'gold' },
      { label: 'Total Loaded Miles', value: rows.reduce((s, r) => s + r['Loaded Miles'], 0).toLocaleString(), color: 'positive' },
      { label: 'Total Miles', value: totalMiles.toLocaleString(), color: '' },
    ];
    const html = generatePrintHTML('Quarterly IFTA Report', `${quarter} ${year}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Quarter</label>
            <select value={quarter} onChange={e => setQuarter(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs">
              {['Q1', 'Q2', 'Q3', 'Q4'].map(q => <option key={q} value={q} className="bg-background">{q}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Year</label>
            <select value={year} onChange={e => setYear(Number(e.target.value))}
              className="px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs">
              {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y} className="bg-background">{y}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportCSV(csvRows, `ifta-${quarter}-${year}.csv`)} disabled={rows.length === 0}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/20 border border-primary/40 text-primary text-xs font-semibold hover:bg-primary/30 transition-colors disabled:opacity-40">
            Export IFTA CSV
          </button>
          <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
          <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
        </div>
      </div>
      <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3">
        <p className="text-xs text-amber-400 font-semibold">⚠ Review with your IFTA administrator before filing.</p>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['State', 'Loaded Miles', 'Empty Miles', 'Total Miles'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(r => (
              <tr key={r.State} className="hover:bg-white/3">
                <td className="px-3 py-2.5 font-semibold text-foreground">{r.State}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Loaded Miles'].toLocaleString()}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Empty Miles'].toLocaleString()}</td>
                <td className="px-3 py-2.5 font-semibold text-foreground">{r['Total Miles'].toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-border bg-white/5 font-bold">
                <td className="px-3 py-2.5 text-foreground">TOTAL</td>
                <td colSpan={2}></td>
                <td className="px-3 py-2.5 text-foreground">{totalMiles.toLocaleString()}</td>
              </tr>
            </tfoot>
          )}
        </table>
        {rows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No IFTA data for {quarter} {year}.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
    </div>
  );
}